import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, showErrorToast } from '@/components';
import { ApiKeyEnvironment } from '@/services/api/apiKeys';
import {
  createWebhookEndpoint,
  CreateWebhookEndpointResponse,
  SHIPMENT_WEBHOOK_EVENTS,
  WebhookEndpointRow,
} from '@/services/api/webhooks';
import { extractApiErrorMessage } from '@/utils/errorHandler';

interface AddWebhookEndpointModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /**
   * The endpoint already active in each environment. One active endpoint per
   * environment: a taken environment cannot be picked — its card says which
   * endpoint to disable first.
   */
  activeByEnv?: Partial<Record<ApiKeyEnvironment, WebhookEndpointRow>>;
}

const ENV_OPTIONS: {
  value: ApiKeyEnvironment;
  label: string;
  blurb: string;
}[] = [
  {
    value: 'TEST',
    label: 'Sandbox',
    blurb:
      'Receives only sandbox events — pair it with your dash_test_… key while building.',
  },
  {
    value: 'LIVE',
    label: 'Production',
    blurb:
      'Receives only production events. Register it when your handler is ready for real traffic.',
  },
];

/**
 * Two-phase modal, mirroring CreateApiKeyModal: the form, then — on success —
 * the one-time signing-secret reveal. No backdrop close while the secret is on
 * screen: once dismissed it is gone forever (there is no reveal endpoint).
 */
const AddWebhookEndpointModal: React.FC<AddWebhookEndpointModalProps> = ({
  open,
  onClose,
  onCreated,
  activeByEnv = {},
}) => {
  const [url, setUrl] = useState('');
  const [environment, setEnvironment] = useState<ApiKeyEnvironment>('TEST');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreateWebhookEndpointResponse | null>(
    null
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl('');
      // Start on the first environment that is still free (Sandbox first).
      setEnvironment(activeByEnv.TEST && !activeByEnv.LIVE ? 'LIVE' : 'TEST');
      setSubmitting(false);
      setCreated(null);
      setCopied(false);
    }
    // Reset on OPEN only. `activeByEnv` changes right after a create (the
    // section reloads its list): re-running then would wipe the one-time
    // secret off the screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const validUrl = /^https:\/\/.+/i.test(url.trim());
  const taken = activeByEnv[environment];

  const submit = async () => {
    if (!validUrl || taken || submitting) return;
    setSubmitting(true);
    try {
      const res = await createWebhookEndpoint({
        url: url.trim(),
        environment,
      });
      setCreated(res);
      onCreated();
    } catch (err) {
      showErrorToast(
        'Failed to add endpoint',
        extractApiErrorMessage(err, 'Failed to add endpoint')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (permissions) — the secret stays selectable.
    }
  };

  const closeReveal = () => {
    setCreated(null);
    onClose();
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        // While the one-time secret is on screen, a stray backdrop click must
        // not destroy it — closing is explicit only.
        onClick={created || submitting ? undefined : onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-bg-white-0 shadow-card-lg">
          {created ? (
            /* ── Phase 2: one-time secret reveal ──────────────────── */
            <div className="px-6 py-5">
              <div className="flex items-center gap-2">
                <i className="ri-broadcast-line text-lg text-success-base" />
                <h2 className="text-lg font-semibold text-text-strong-950">
                  Endpoint registered
                </h2>
              </div>
              <p className="mt-2 text-sm text-text-sub-600">
                Every delivery to{' '}
                <span className="break-all font-mono text-xs text-text-strong-950">
                  {created.url}
                </span>{' '}
                will be signed with this secret:
              </p>
              <div className="mt-3 rounded-lg bg-bg-strong-950 px-4 py-3">
                <code className="break-all font-mono text-sm text-static-white">
                  {created.secret}
                </code>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-warning-lighter px-3 py-2.5 text-xs text-warning-dark">
                <i className="ri-error-warning-line mt-px" />
                <span>
                  <b>This secret is shown only once.</b> Store it in your secret
                  manager now — you need it to verify the{' '}
                  <code className="font-mono">x-dash-signature</code> header on
                  each delivery. If it&apos;s lost, disable this endpoint and
                  register it again.
                </span>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="white" onClick={copySecret}>
                  <i className="ri-file-copy-line mr-1.5" />
                  {copied ? 'Copied ✓' : 'Copy secret'}
                </Button>
                <Button onClick={closeReveal}>
                  I&apos;ve stored it safely
                </Button>
              </div>
            </div>
          ) : (
            /* ── Phase 1: the form ────────────────────────────────── */
            <>
              <div className="border-b border-stroke-soft-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-text-strong-950">
                  Add webhook endpoint
                </h2>
                <p className="mt-0.5 text-xs text-text-sub-600">
                  Dash will POST every shipment event to this URL. One active
                  endpoint per environment.
                </p>
              </div>

              <div className="space-y-5 px-6 py-5">
                {/* URL */}
                <div>
                  <label
                    htmlFor="webhook-url"
                    className="mb-1.5 block text-xs font-medium text-text-sub-600"
                  >
                    Endpoint URL
                  </label>
                  <input
                    id="webhook-url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://your-system.example/webhooks/dash"
                    className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 font-mono text-sm text-text-strong-950 placeholder:font-sans placeholder:text-text-soft-400 focus:border-primary focus:outline-none"
                  />
                  {url.trim() !== '' && !validUrl && (
                    <p className="mt-1 text-[11px] text-error-base">
                      Must be an https:// URL — plain http is rejected.
                    </p>
                  )}
                </div>

                {/* Environment */}
                <div>
                  <span className="mb-1.5 block text-xs font-medium text-text-sub-600">
                    Environment
                  </span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ENV_OPTIONS.map((opt) => {
                      const active = environment === opt.value;
                      const inUse = activeByEnv[opt.value];
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setEnvironment(opt.value)}
                          aria-pressed={active}
                          className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                            active
                              ? 'border-primary bg-(--primary-alpha-10)'
                              : 'border-stroke-soft-200 hover:border-stroke-sub-300'
                          }`}
                        >
                          <span className="text-sm font-semibold text-text-strong-950">
                            {opt.label}
                          </span>
                          <span className="mt-1 block text-[11px] leading-relaxed text-text-sub-600">
                            {inUse
                              ? 'Already has an active endpoint.'
                              : opt.blurb}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {taken && (
                    <p
                      role="alert"
                      className="mt-2 rounded-lg bg-warning-lighter px-3 py-2 text-[11px] leading-relaxed text-warning-dark"
                    >
                      You already have an active{' '}
                      {ENV_OPTIONS.find((o) => o.value === environment)?.label}{' '}
                      endpoint (
                      <span className="break-all font-mono">{taken.url}</span>
                      ). Only one active endpoint per environment is allowed —
                      disable it first, then add the new one.
                    </p>
                  )}
                </div>

                {/* What it receives — informational, not a picker. */}
                <div className="rounded-lg border border-stroke-soft-200 p-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-text-soft-400">
                    Events this endpoint will receive
                  </span>
                  <p className="mt-1 text-[11px] leading-relaxed text-text-sub-600">
                    All shipment events — every status change; there is nothing
                    to pick. Ignore any value you don&apos;t handle.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SHIPMENT_WEBHOOK_EVENTS.filter(
                      // AT_HUB has two messages (scan / assigned hub): one chip.
                      (e, i, all) =>
                        all.findIndex((o) => o.status === e.status) === i
                    ).map((e) => (
                      <code
                        key={e.status}
                        title={e.when}
                        className="rounded bg-bg-weak-50 px-1.5 py-0.5 font-mono text-[11px] text-text-sub-600"
                      >
                        {e.status}
                      </code>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-stroke-soft-200 px-6 py-4">
                <Button variant="white" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={!validUrl || !!taken || submitting}
                >
                  {submitting ? 'Adding…' : 'Add endpoint'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddWebhookEndpointModal;
